import React, { useContext, useMemo, useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  FlatList,
  StyleSheet,
  TouchableOpacity,
  Modal,
  ScrollView,
  Animated,
  StatusBar,
  Dimensions,
  Alert,
  ActivityIndicator,
  TextInput,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { SocketContext } from '../context/SocketContext';
import CONFIG from '../config/Config';

const { width: SCREEN_W } = Dimensions.get('window');

// Açık temaya uygun renk paleti
const COLORS = {
  bg: '#FFFFFF',
  surface: '#F8F9FA',
  surfaceHover: '#F0F0F0',
  border: '#E5E7EB',
  borderLight: '#F3F4F6',
  accent: '#0066FF',
  accentDim: 'rgba(0,102,255,0.12)',
  accentText: '#0052CC',
  kurye: '#0099FF',
  kuryeDim: 'rgba(0,153,255,0.12)',
  gelal: '#FF8800',
  gelalDim: 'rgba(255,136,0,0.12)',
  new: '#00CC66',
  newDim: 'rgba(0,204,102,0.12)',
  textPrimary: '#111827',
  textSecondary: '#4B5563',
  textTertiary: '#9CA3AF',
  danger: '#EF4444',
  dangerDim: 'rgba(239,68,68,0.12)',
};

function initials(name) {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  return (parts[0][0] + (parts[parts.length - 1][0] || '')).toUpperCase();
}

function fmtPrice(v) {
  const n = parseFloat(v || 0);
  return '₺' + n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtTime(v) {
  if (!v) return '--:--';
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
}

function fmtDate(v) {
  if (!v) return '-';
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleString('tr-TR');
}

function timeDiffMin(v) {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  const diff = Math.floor((Date.now() - d.getTime()) / 60000);
  if (diff < 1) return 'Az önce';
  if (diff < 60) return `${diff} dk önce`;
  return `${Math.floor(diff / 60)} sa önce`;
}

// Yeni veri yapısına göre ürün alt bilgisini (gramaj/dilim mantığı) formatlar
function getUrunAltMetin(u) {
  const miktarStr = `${u.miktar} ${u.birim}`;
  let ekBilgi = '';

  if (u.tip === 'slice') {
    ekBilgi = u.gramAgirligi ? `${u.gramAgirligi}g / dilim` : '';
  } else if (u.tip === 'kangal') {
    ekBilgi = u.gramAgirligi ? `${u.gramAgirligi}g / adet` : '';
  } else {
    // weight vb. normal tartılı ürünler
    ekBilgi = u.gramAgirligi ? `Net: ${u.gramAgirligi}g` : '';
  }

  return ekBilgi ? `${miktarStr}  •  ${ekBilgi}` : miktarStr;
}

// ─── Order list item ─────────────────────────────────────────────────────────
function OrderItem({ item, onPress, index }) {
  const anim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(anim, {
      toValue: 1,
      duration: 280,
      delay: index * 55,
      useNativeDriver: true,
    }).start();
  }, []);

  const isKurye = item.teslimatTipi === 'kurye';
  const tipColor = isKurye ? COLORS.kurye : COLORS.gelal;
  const tipBg = isKurye ? COLORS.kuryeDim : COLORS.gelalDim;

  return (
    <Animated.View
      style={{
        opacity: anim,
        transform: [{ translateY: anim.interpolate({ inputRange: [0, 1], outputRange: [18, 0] }) }],
      }}
    >
      <TouchableOpacity
        style={[
          styles.orderCard,
          item._durum === 'kabul' && styles.orderCardAccepted,
          item._durum === 'red' && styles.orderCardRejected,
          !item._durum && styles.orderCardPending,
        ]}
        onPress={() => onPress(item)}
        activeOpacity={0.75}
      >
        {!item._durum && <View style={styles.pendingPulse} />}
        {item._durum === 'kabul' && <View style={styles.acceptedPulse} />}
        {item._durum === 'red' && <View style={styles.rejectedPulse} />}

        <View style={[styles.avatar, { backgroundColor: tipBg }]}>
          <Text style={[styles.avatarText, { color: tipColor }]}>{initials(item.adSoyad)}</Text>
        </View>

        <View style={styles.orderBody}>
          <View style={styles.orderNameRow}>
            <Text style={styles.orderName} numberOfLines={1}>
              {item.adSoyad || 'Müşteri'}
            </Text>
            {item._durum === 'kabul' ? (
              <View style={styles.acceptedBadge}>
                <Text style={styles.acceptedBadgeText}>ONAYLANDI</Text>
              </View>
            ) : item._durum === 'red' ? (
              <View style={styles.rejectedBadge}>
                <Text style={styles.rejectedBadgeText}>REDDEDİLDİ</Text>
              </View>
            ) : (
              <View style={styles.pendingBadge}>
                <Text style={styles.pendingBadgeText}>BEKLEMEDE</Text>
              </View>
            )}
          </View>

          <Text style={styles.orderMeta} numberOfLines={1}>
            #{item.siparisId} · {timeDiffMin(item.siparisTarihi)}
          </Text>

          <View style={styles.orderTagRow}>
            <View style={[styles.tipBadge, { backgroundColor: tipBg }]}>
              <View style={[styles.tipDot, { backgroundColor: tipColor }]} />
              <Text style={[styles.tipText, { color: tipColor }]}>
                {isKurye ? 'Kurye' : 'Gel-Al'}
              </Text>
            </View>
            {item.sepet?.length > 0 && (
              <Text style={styles.itemCount}>{item.sepet.length} ürün</Text>
            )}
          </View>
        </View>

        <View style={styles.orderRight}>
          <Text style={styles.orderPrice}>{fmtPrice(item.toplamTutar)}</Text>
          <Text style={styles.orderTime}>{fmtTime(item.siparisTarihi)}</Text>
        </View>
      </TouchableOpacity>
    </Animated.View>
  );
}

// ─── Drawer detail ───────────────────────────────────────────────────────────
function DetailDrawer({ item, onClose, onStatusUpdate, onRemoveOrder }) {
  const slideAnim = useRef(new Animated.Value(400)).current;
  const backdropAnim = useRef(new Animated.Value(0)).current;

  // Yüklenme state'leri
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [showRedModal, setShowRedModal] = useState(false);
  const [redSebebi, setRedSebebi] = useState('');

  useEffect(() => {
    Animated.parallel([
      Animated.spring(slideAnim, { toValue: 0, damping: 22, stiffness: 200, useNativeDriver: true }),
      Animated.timing(backdropAnim, { toValue: 1, duration: 250, useNativeDriver: true }),
    ]).start();
  }, []);

  function handleClose() {
    Animated.parallel([
      Animated.timing(slideAnim, { toValue: 400, duration: 220, useNativeDriver: true }),
      Animated.timing(backdropAnim, { toValue: 0, duration: 200, useNativeDriver: true }),
    ]).start(() => onClose());
  }

  // YAZDIRMA FONKSİYONU
  const fisYazdir = async () => {
    setIsPrinting(true);
    const siparisPaketi = {
      musteriAdi: item.adSoyad || 'Bilinmeyen Müşteri',
      kasiyer: 'Online Sistem', 
      telefon: item.telefon || '',
      adres: item.teslimatTipi === 'kurye' ? item.adres : 
        'GEL-AL (Mağaza Teslim)' + (item.gelisSaati ? ' - Saat: ' + fmtTime(item.gelisSaati) : ''),
      tarih: fmtDate(item.siparisTarihi),
      urunler: item.sepet.map(u => ({
        ad: u.urunAdi,
        detay: getUrunAltMetin(u),
        tutar: fmtPrice(u.toplam),
      })),
      toplamTutar: fmtPrice(item.toplamTutar),
    };

    try {
      const response = await fetch(`${CONFIG.API_BASE_URL}/yazdir-paket`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-App-Env': CONFIG.ENV },
        body: JSON.stringify(siparisPaketi),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Sunucu hatası oluştu.');
      }

      // Başarılı mesajı eklendi:
      Alert.alert('Başarılı!', 'Adisyon başarıyla yazdırıldı.');

    } catch (error) {
      Alert.alert(
        'Yazdırma Hatası',
        'Fiş yazdırılamadı: ' + error.message,
        [
          { text: 'Kapat', style: 'cancel' },
          { text: 'Tekrar Dene', onPress: () => fisYazdir() }
        ]
      );
    } finally {
      setIsPrinting(false);
    }
  };

  // SİPARİŞ DURUM GÜNCELLEME (KABUL / RED)
  const handleOrderStatus = async (actionType, sebep) => {
    setIsProcessing(true);
    
    try {
      const body = {
        siparisId: item.siparisId,
        durum: actionType,
      };
      if (actionType === 'red' && sebep) {
        body.redSebebi = sebep;
      }

      const response = await fetch(`${CONFIG.API_BASE_URL}/siparis-guncelle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-App-Env': CONFIG.ENV },
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error('Sipariş durumu güncellenemedi.');

      // Sipariş durumunu local state'te güncelle ve listeden kaldır
      onRemoveOrder(item.siparisId);

      // Eğer kabul edildiyse otomatik fiş yazdır
      if (actionType === 'kabul') {
        await fisYazdir();
      }

      handleClose(); // İşlem bitince drawer'ı kapat
    } catch (error) {
      Alert.alert('Hata', error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  // Red onay modalını aç
  const handleReddet = () => {
    setRedSebebi('');
    setShowRedModal(true);
  };

  // Red işlemini onayla
  const confirmReddet = () => {
    setShowRedModal(false);
    handleOrderStatus('red', redSebebi.trim());
  };

  const isKurye = item.teslimatTipi === 'kurye';
  const tipColor = isKurye ? COLORS.kurye : COLORS.gelal;

  return (
    <Modal visible transparent animationType="none" onRequestClose={handleClose}>
      <Animated.View style={[styles.backdrop, { opacity: backdropAnim }]}>
        <TouchableOpacity style={StyleSheet.absoluteFill} onPress={handleClose} disabled={isProcessing} />
      </Animated.View>

      <Animated.View
        style={[styles.drawer, { transform: [{ translateY: slideAnim }] }]}
      >
        <View style={styles.drawerHandle} />

        <View style={styles.drawerHeader}>
          <View>
            <Text style={styles.drawerTitle}>{item.adSoyad || 'Müşteri'}</Text>
            <Text style={styles.drawerSub}>Sipariş #{item.siparisId}</Text>
          </View>
          <View style={[styles.tipBadgeLg, { backgroundColor: isKurye ? COLORS.kuryeDim : COLORS.gelalDim }]}>
            <Text style={[styles.tipBadgeLgText, { color: tipColor }]}>
              {isKurye ? '🛵 Kurye' : '🏪 Gel-Al'}
            </Text>
          </View>
        </View>

        <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: 20 }}>
          {/* Info rows */}
          <View style={styles.infoBlock}>
            {[
              ['Tarih', fmtDate(item.siparisTarihi)],
              ['Telefon', item.telefon || '-'],
              item.teslimatTipi !== 'kurye' && item.gelisSaati ? ['Geliş saati', fmtTime(item.gelisSaati)] : null,
              isKurye ? ['Adres', item.adres || '-'] : null,
            ]
              .filter(Boolean)
              .map(([k, v]) => (
                <View key={k} style={styles.infoRow}>
                  <Text style={styles.infoKey}>{k}</Text>
                  <Text style={styles.infoVal}>{v}</Text>
                </View>
              ))}
          </View>

          {/* Sepet */}
          {Array.isArray(item.sepet) && item.sepet.length > 0 && (
            <View style={styles.sepetBlock}>
              <Text style={styles.sectionLabel}>ÜRÜNLER</Text>
              {item.sepet.map((u, i) => (
                <View key={`${u.urunId}-${i}`} style={styles.urunCard}>
                  <View style={styles.urunLeft}>
                    <Text style={styles.urunName}>{u.urunAdi}</Text>
                    <Text style={styles.urunMeta}>{getUrunAltMetin(u)}</Text>
                  </View>
                  <View style={styles.urunRight}>
                    <Text style={styles.urunTotal}>{fmtPrice(u.toplam)}</Text>
                    <Text style={styles.urunUnit}>{fmtPrice(u.birimFiyat)} / kg</Text>
                  </View>
                </View>
              ))}
            </View>
          )}

          {/* Toplam */}
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Toplam Tutar</Text>
            <Text style={styles.totalValue}>{fmtPrice(item.toplamTutar)}</Text>
          </View>
        </ScrollView>

        {/* AKSİYON BUTONLARI */}
        <View style={styles.actionRow}>
          {/* Manuel Yazdır Butonu */}
          <TouchableOpacity 
            style={styles.btnPrint} 
            onPress={fisYazdir}
            disabled={isPrinting || isProcessing}
          >
            {isPrinting ? <ActivityIndicator color={COLORS.textPrimary} size="small" /> : <Text style={styles.btnPrintIcon}>🖨️</Text>}
          </TouchableOpacity>

          {item._durum === 'kabul' ? (
            <View style={styles.btnAcceptedStatus}>
              <Text style={styles.btnAcceptedStatusText}>✅ Sipariş Onaylandı</Text>
            </View>
          ) : item._durum === 'red' ? (
            <View style={styles.btnRejectedStatus}>
              <Text style={styles.btnRejectedStatusText}>❌ Sipariş Reddedildi</Text>
            </View>
          ) : (
            <>
              {/* Reddet Butonu */}
              <TouchableOpacity 
                style={styles.btnReject} 
                onPress={handleReddet}
                disabled={isProcessing}
              >
                <Text style={styles.btnRejectText}>Reddet</Text>
              </TouchableOpacity>

              {/* Kabul Et Butonu */}
              <TouchableOpacity 
                style={styles.btnAccept} 
                onPress={() => handleOrderStatus('kabul')}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <ActivityIndicator color="#FFF" size="small" />
                ) : (
                  <Text style={styles.btnAcceptText}>Kabul Et & Yazdır</Text>
                )}
              </TouchableOpacity>
            </>
          )}
        </View>

        {/* RED ONAY MODALI */}
        <Modal visible={showRedModal} transparent animationType="fade">
          <KeyboardAvoidingView
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
            style={styles.redModalOverlay}
          >
            <View style={styles.redModalCard}>
              <Text style={styles.redModalIcon}>⚠️</Text>
              <Text style={styles.redModalTitle}>Siparişi Reddet</Text>
              <Text style={styles.redModalDesc}>
                #{item.siparisId} numaralı siparişi reddetmek istediğinize emin misiniz?
              </Text>

              <Text style={styles.redModalLabel}>Red Sebebi</Text>
              <TextInput
                style={styles.redModalInput}
                placeholder="Sebep yazınız (opsiyonel)"
                placeholderTextColor={COLORS.textTertiary}
                value={redSebebi}
                onChangeText={setRedSebebi}
                multiline
                numberOfLines={3}
                textAlignVertical="top"
              />

              <View style={styles.redModalBtnRow}>
                <TouchableOpacity
                  style={styles.redModalBtnCancel}
                  onPress={() => setShowRedModal(false)}
                >
                  <Text style={styles.redModalBtnCancelText}>Vazgeç</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.redModalBtnConfirm}
                  onPress={confirmReddet}
                >
                  <Text style={styles.redModalBtnConfirmText}>Evet, Reddet</Text>
                </TouchableOpacity>
              </View>
            </View>
          </KeyboardAvoidingView>
        </Modal>

      </Animated.View>
    </Modal>
  );
}

// ─── Main screen ─────────────────────────────────────────────────────────────
export default function OnlineSiparislerScreen({ route }) {
  const { siparisler = [], connected, siparisOnayla, siparisGuncelle } = useContext(SocketContext);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('yeni');
  const [reddedilenler, setReddedilenler] = useState([]);
  const [kabulEdilenler, setKabulEdilenler] = useState([]);
  const [redLoading, setRedLoading] = useState(false);
  const [kabulLoading, setKabulLoading] = useState(false);
  const connAnim = useRef(new Animated.Value(1)).current;
  const lastOpenedOrderRef = useRef(null);

  // Reddedilen siparişleri backend'den çek
  const fetchReddedilenler = async () => {
    setRedLoading(true);
    try {
      const res = await fetch(`${CONFIG.API_BASE_URL}/rejected-orders`, {
        headers: { 'x-internal-secret': '190717hasancinaralti', 'X-App-Env': CONFIG.ENV }
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.siparisler)) {
        setReddedilenler(data.siparisler);
      }
    } catch (e) {
      console.log('[ORDERS] Reddedilenler fetch hatası:', e?.message);
    } finally {
      setRedLoading(false);
    }
  };

  // Kabul edilen siparişleri backend'den çek
  const fetchKabulEdilenler = async () => {
    setKabulLoading(true);
    try {
      const res = await fetch(`${CONFIG.API_BASE_URL}/accepted-orders`, {
        headers: { 'x-internal-secret': '190717hasancinaralti', 'X-App-Env': CONFIG.ENV }
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.siparisler)) {
        setKabulEdilenler(data.siparisler);
      }
    } catch (e) {
      console.log('[ORDERS] Kabul edilenler fetch hatası:', e?.message);
    } finally {
      setKabulLoading(false);
    }
  };

  // Tab değişince ilgili veriyi çek
  useEffect(() => {
    if (filter === 'reddedilen') fetchReddedilenler();
    if (filter === 'kabul') fetchKabulEdilenler();
  }, [filter]);

  // Ekran açılınca her iki listeyi de çek (tab sayıları görünsün)
  useEffect(() => {
    fetchReddedilenler();
    fetchKabulEdilenler();
  }, []);

  // Pulse animation for live dot
  useEffect(() => {
    if (!connected) return;
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(connAnim, { toValue: 0.3, duration: 900, useNativeDriver: true }),
        Animated.timing(connAnim, { toValue: 1, duration: 900, useNativeDriver: true }),
      ])
    );
    loop.start();
    return () => loop.stop();
  }, [connected]);

  const filtered = useMemo(() => {
    if (filter === 'reddedilen') return reddedilenler;
    if (filter === 'kabul') return kabulEdilenler;
    return [...siparisler];
  }, [siparisler, reddedilenler, kabulEdilenler, filter]);

  useEffect(() => {
    const openOrderId = route?.params?.openOrderId;
    if (!openOrderId || lastOpenedOrderRef.current === String(openOrderId)) {
      return;
    }

    const matchedOrder = siparisler.find((order) => String(order.siparisId) === String(openOrderId));
    if (!matchedOrder) {
      return;
    }

    setFilter('yeni');
    setSelected(matchedOrder);
    lastOpenedOrderRef.current = String(openOrderId);
  }, [route?.params?.openOrderId, siparisler]);

  const FILTERS = ['yeni', 'kabul', 'reddedilen'];

  const filterLabels = {
    'yeni': `Yeni${siparisler.filter(s => !s._durum).length > 0 ? ` (${siparisler.filter(s => !s._durum).length})` : ''}`,
    'kabul': `Kabul${kabulEdilenler.length > 0 ? ` (${kabulEdilenler.length})` : ''}`,
    'reddedilen': `Red${reddedilenler.length > 0 ? ` (${reddedilenler.length})` : ''}`,
  };

  return (
    <View style={styles.container}>
      <StatusBar barStyle="dark-content" backgroundColor={COLORS.bg} />

      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Siparişler</Text>
        <View style={[styles.connBadge, { backgroundColor: connected ? COLORS.newDim : COLORS.dangerDim }]}>
          <Animated.View
            style={[
              styles.connDot,
              { backgroundColor: connected ? COLORS.new : COLORS.danger, opacity: connAnim },
            ]}
          />
          <Text style={[styles.connText, { color: connected ? COLORS.new : COLORS.danger }]}>
            {connected ? 'Canlı' : 'Bağlantı Yok'}
          </Text>
        </View>
      </View>

      {/* Filter tabs */}
      <View style={styles.filterRow}>
        {FILTERS.map((f) => (
          <TouchableOpacity
            key={f}
            style={[
              styles.filterTab,
              filter === f && styles.filterTabActive,
              f === 'reddedilen' && filter === f && styles.filterTabRed,
              f === 'kabul' && filter === f && styles.filterTabGreen,
            ]}
            onPress={() => setFilter(f)}
            activeOpacity={0.7}
          >
            <Text style={[
              styles.filterTabText,
              filter === f && styles.filterTabTextActive,
              f === 'reddedilen' && filter === f && styles.filterTabTextRed,
              f === 'kabul' && filter === f && styles.filterTabTextGreen,
            ]}>
              {filterLabels[f]}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* List */}
      {(redLoading && filter === 'reddedilen') || (kabulLoading && filter === 'kabul') ? (
        <View style={styles.empty}>
          <ActivityIndicator size="large" color={COLORS.accent} />
        </View>
      ) : filtered.length === 0 ? (
        <View style={styles.empty}>
          <Text style={styles.emptyIcon}>{filter === 'reddedilen' ? '❌' : filter === 'kabul' ? '✅' : '📋'}</Text>
          <Text style={styles.emptyText}>
            {filter === 'reddedilen' ? 'Bugün reddedilen sipariş yok' : filter === 'kabul' ? 'Bugün kabul edilen sipariş yok' : 'Bekleyen sipariş yok'}
          </Text>
        </View>
      ) : (
        <FlatList
          data={filtered}
          keyExtractor={(item) => String(item.siparisId)}
          renderItem={({ item, index }) => (
            <OrderItem item={item} onPress={setSelected} index={index} />
          )}
          contentContainerStyle={styles.listContent}
          showsVerticalScrollIndicator={false}
        />
      )}

      {/* Detail drawer */}
      {selected && <DetailDrawer item={selected} onClose={() => setSelected(null)} onStatusUpdate={siparisOnayla} onRemoveOrder={siparisGuncelle} />}
    </View>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: COLORS.bg },

  // Header
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingTop: 60,
    paddingBottom: 20,
  },
  headerTitle: { fontSize: 28, fontWeight: '700', color: COLORS.textPrimary, letterSpacing: -0.5 },
  connBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
  },
  connDot: { width: 7, height: 7, borderRadius: 4 },
  connText: { fontSize: 12, fontWeight: '600' },

  // Filters
  filterRow: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    gap: 8,
    marginBottom: 16,
  },
  filterTab: {
    paddingHorizontal: 16,
    paddingVertical: 7,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: COLORS.border,
    backgroundColor: COLORS.surface,
  },
  filterTabActive: {
    backgroundColor: COLORS.accentDim,
    borderColor: COLORS.accent,
  },
  filterTabText: { fontSize: 13, fontWeight: '500', color: COLORS.textSecondary },
  filterTabTextActive: { color: COLORS.accent },
  filterTabRed: {
    backgroundColor: COLORS.dangerDim,
    borderColor: COLORS.danger,
  },
  filterTabTextRed: { color: COLORS.danger },
  filterTabGreen: {
    backgroundColor: COLORS.newDim,
    borderColor: COLORS.new,
  },
  filterTabTextGreen: { color: COLORS.new },

  // List
  listContent: { paddingHorizontal: 16, paddingBottom: 32, gap: 10 },

  // Order card
  orderCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.surface,
    borderRadius: 16,
    padding: 14,
    borderWidth: 1,
    borderColor: COLORS.border,
    gap: 12,
    overflow: 'hidden',
  },
  orderCardAccepted: {
    borderColor: COLORS.new,
    borderWidth: 1,
    opacity: 0.65,
  },
  orderCardPending: {
    borderColor: COLORS.gelal,
    borderWidth: 1,
  },
  pendingPulse: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: 4,
    height: '100%',
    backgroundColor: COLORS.gelal,
    borderTopLeftRadius: 16,
    borderBottomLeftRadius: 16,
  },
  acceptedPulse: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: 4,
    height: '100%',
    backgroundColor: COLORS.new,
    borderTopLeftRadius: 16,
    borderBottomLeftRadius: 16,
  },
  orderCardRejected: {
    borderColor: COLORS.danger,
    borderWidth: 1,
    opacity: 0.55,
  },
  rejectedPulse: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: 4,
    height: '100%',
    backgroundColor: COLORS.danger,
    borderTopLeftRadius: 16,
    borderBottomLeftRadius: 16,
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  avatarText: { fontSize: 14, fontWeight: '700' },
  orderBody: { flex: 1, gap: 4 },
  orderNameRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  orderName: { fontSize: 14, fontWeight: '600', color: COLORS.textPrimary, flex: 1 },
  acceptedBadge: {
    backgroundColor: COLORS.newDim,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  acceptedBadgeText: { fontSize: 9, fontWeight: '700', color: COLORS.new, letterSpacing: 0.5 },
  pendingBadge: {
    backgroundColor: COLORS.gelalDim,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  pendingBadgeText: { fontSize: 9, fontWeight: '700', color: COLORS.gelal, letterSpacing: 0.5 },
  rejectedBadge: {
    backgroundColor: COLORS.dangerDim,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  rejectedBadgeText: { fontSize: 9, fontWeight: '700', color: COLORS.danger, letterSpacing: 0.5 },
  orderMeta: { fontSize: 12, color: COLORS.textTertiary },
  orderTagRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 2 },
  tipBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
  },
  tipDot: { width: 5, height: 5, borderRadius: 3 },
  tipText: { fontSize: 11, fontWeight: '600' },
  itemCount: { fontSize: 11, color: COLORS.textTertiary },
  orderRight: { alignItems: 'flex-end', gap: 4, flexShrink: 0 },
  orderPrice: { fontSize: 15, fontWeight: '700', color: COLORS.textPrimary },
  orderTime: { fontSize: 11, color: COLORS.textTertiary },

  // Empty
  empty: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 12 },
  emptyIcon: { fontSize: 40 },
  emptyText: { fontSize: 15, color: COLORS.textTertiary },

  // Backdrop
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.4)',
  },

  // Drawer
  drawer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: COLORS.bg,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    maxHeight: '85%',
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: 0,
    borderTopWidth: 1,
    borderColor: COLORS.borderLight,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 20,
  },
  drawerHandle: {
    width: 40,
    height: 4,
    backgroundColor: '#D1D5DB',
    borderRadius: 2,
    alignSelf: 'center',
    marginBottom: 20,
  },
  drawerHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 20,
  },
  drawerTitle: { fontSize: 20, fontWeight: '700', color: COLORS.textPrimary },
  drawerSub: { fontSize: 13, color: COLORS.textTertiary, marginTop: 3 },
  tipBadgeLg: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 10 },
  tipBadgeLgText: { fontSize: 13, fontWeight: '600' },

  // Info block
  infoBlock: {
    backgroundColor: COLORS.surface,
    borderRadius: 14,
    overflow: 'hidden',
    marginBottom: 16,
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.border,
    gap: 12,
  },
  infoKey: { fontSize: 13, color: COLORS.textSecondary, flexShrink: 0 },
  infoVal: { fontSize: 13, color: COLORS.textPrimary, textAlign: 'right', flex: 1 },

  // Sepet
  sepetBlock: { marginBottom: 16 },
  sectionLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: COLORS.textTertiary,
    letterSpacing: 1,
    marginBottom: 10,
  },
  urunCard: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: COLORS.surface,
    borderRadius: 12,
    padding: 14,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  urunLeft: { flex: 1, gap: 4 },
  urunName: { fontSize: 14, fontWeight: '600', color: COLORS.textPrimary },
  urunMeta: { fontSize: 12, color: COLORS.textSecondary },
  urunRight: { alignItems: 'flex-end', gap: 4 },
  urunTotal: { fontSize: 15, fontWeight: '700', color: COLORS.textPrimary },
  urunUnit: { fontSize: 11, color: COLORS.textTertiary },

  // Total
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: COLORS.borderLight,
    marginTop: 4,
  },
  totalLabel: { fontSize: 14, fontWeight: '600', color: COLORS.textSecondary },
  totalValue: { fontSize: 22, fontWeight: '700', color: COLORS.accent },

  // Action Buttons
  actionRow: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 10,
    marginBottom: 32,
  },
  btnPrint: {
    backgroundColor: COLORS.surfaceHover,
    borderRadius: 14,
    width: 56,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  btnPrintIcon: {
    fontSize: 22,
  },
  btnReject: {
    flex: 1,
    backgroundColor: COLORS.dangerDim,
    borderRadius: 14,
    paddingVertical: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  btnRejectText: {
    fontSize: 15,
    fontWeight: '700',
    color: COLORS.danger,
  },
  btnAccept: {
    flex: 2,
    backgroundColor: COLORS.new,
    borderRadius: 14,
    paddingVertical: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  btnAcceptText: {
    fontSize: 15,
    fontWeight: '700',
    color: '#FFF',
  },
  btnAcceptedStatus: {
    flex: 3,
    backgroundColor: COLORS.newDim,
    borderRadius: 14,
    paddingVertical: 14,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: COLORS.new,
  },
  btnAcceptedStatusText: {
    fontSize: 15,
    fontWeight: '700',
    color: COLORS.new,
  },
  btnRejectedStatus: {
    flex: 3,
    backgroundColor: COLORS.dangerDim,
    borderRadius: 14,
    paddingVertical: 14,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: COLORS.danger,
  },
  btnRejectedStatusText: {
    fontSize: 15,
    fontWeight: '700',
    color: COLORS.danger,
  },

  // Red Onay Modalı
  redModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  redModalCard: {
    backgroundColor: COLORS.bg,
    borderRadius: 20,
    padding: 24,
    width: '100%',
    maxWidth: 380,
    alignItems: 'center',
  },
  redModalIcon: {
    fontSize: 40,
    marginBottom: 12,
  },
  redModalTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: COLORS.textPrimary,
    marginBottom: 8,
  },
  redModalDesc: {
    fontSize: 14,
    color: COLORS.textSecondary,
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: 20,
  },
  redModalLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: COLORS.textSecondary,
    alignSelf: 'flex-start',
    marginBottom: 8,
  },
  redModalInput: {
    width: '100%',
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 12,
    padding: 14,
    fontSize: 14,
    color: COLORS.textPrimary,
    backgroundColor: COLORS.surface,
    minHeight: 80,
    marginBottom: 20,
  },
  redModalBtnRow: {
    flexDirection: 'row',
    gap: 10,
    width: '100%',
  },
  redModalBtnCancel: {
    flex: 1,
    backgroundColor: COLORS.surface,
    borderRadius: 12,
    paddingVertical: 14,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  redModalBtnCancelText: {
    fontSize: 14,
    fontWeight: '600',
    color: COLORS.textSecondary,
  },
  redModalBtnConfirm: {
    flex: 1,
    backgroundColor: COLORS.danger,
    borderRadius: 12,
    paddingVertical: 14,
    alignItems: 'center',
  },
  redModalBtnConfirmText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFF',
  },
});